import { FC, Fragment, useCallback, useEffect } from "react"

import { CircularProgress } from "@mui/material"

import { NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"

import NoCamera from "../assets/NoCamera.svg?react"
import NoHistoryLogs from "../assets/NoHistoryLogs.svg?react"

import styles from "./Placeholder.module.scss"

type TypographyVariant = React.ComponentProps<typeof Typography>["variant"]

interface Props {
  alertMessage?: string
  openSnackbar?: boolean
  selectionType?: "history" | "camera"
  description?: string
  descriptionVariant?: TypographyVariant
  isScreenPlaceholder?: boolean
  hasBackground?: boolean
  svgHeight?: string
  svgWidth?: string
  isLoading?: boolean
  subDescription?: string
  subDescriptionVariant?: TypographyVariant
}
const Placeholder: FC<Props> = ({
  alertMessage,
  selectionType = "history",
  description,
  descriptionVariant,
  isScreenPlaceholder,
  hasBackground = true,
  svgWidth = "182px",
  svgHeight = "220px",
  openSnackbar,
  isLoading,
  subDescription,
  subDescriptionVariant,
}) => {
  const EmptyStates = {
    history: <NoHistoryLogs className={styles.placeholder} height={svgHeight} width={svgWidth ?? ""} />,
    camera: <NoCamera className={styles.placeholder} height={svgHeight} width={svgWidth ?? ""} />,
  }

  const showAlert = useCallback(() => {
    if (alertMessage && openSnackbar) {
      NotificationUtils.toast(alertMessage, {
        severity: "warning",
      })
    }
  }, [alertMessage, openSnackbar])

  /**
   * Runs the alert logic when the component mounts or showAlert reference changes (e.g. when alertMessage is set).
   */
  useEffect(() => {
    showAlert()
  }, [showAlert])

  return (
    <div
      className={styles.placeholderWrapper}
      style={{
        height: isScreenPlaceholder ? "70vh" : "100%",
        background: hasBackground
          ? isScreenPlaceholder
            ? "var(--base-background-3)"
            : "var(--base-background-1)"
          : "transparent",
      }}
    >
      {isLoading ? (
        <CircularProgress size={70} />
      ) : (
        <Fragment>
          {EmptyStates[selectionType]}
          {description && (
            <div className={styles.descriptionWrapper}>
              <Typography
                variant={descriptionVariant || (isScreenPlaceholder ? "h2-bold" : "label-bold")}
                variantColor={2}
                align="center"
              >
                {description}
              </Typography>
              {subDescription && (
                <Typography variant={subDescriptionVariant || "h3-regular"} variantColor={2} align="center">
                  {subDescription}
                </Typography>
              )}
            </div>
          )}
        </Fragment>
      )}
    </div>
  )
}
export default Placeholder
